/**
 * The session an admin request should carry: refreshed BEFORE it can expire in
 * flight (2026-10-06, #3351).
 *
 * amazon-cognito-identity-js `getSession()` refreshes only an ALREADY expired
 * session (CognitoUser.js: `if (cachedSession.isValid()) return …; else
 * refreshSession(…)`), and `isValid()` accepts a token with one second left. A
 * request sent then reaches API Gateway expired, and the 401 ends the session
 * (session-end.ts errorEndsTheSession) — the operator is signed out in the middle
 * of, say, a bulk grant, with a refresh token that was still good. So: when the
 * ID token has under REFRESH_AHEAD_SECONDS left, refresh first. If that refresh
 * fails, the still-valid session is used, as before; only an expired one is null.
 *
 * Pure (no import.meta.env), so tests drive it with fakes; auth-provider.ts
 * passes `userPool.getCurrentUser()`.
 */

export const REFRESH_AHEAD_SECONDS = 60;

export interface SessionLike {
  isValid(): boolean;
  getIdToken(): { getExpiration(): number };
  /** Present at runtime (CognitoUserSession.js), missing from the library's index.d.ts. */
  getClockDrift?(): number;
  getRefreshToken(): unknown;
}

export interface UserLike<S extends SessionLike> {
  getSession(callback: (err: Error | null, session: S | null) => void): void;
  refreshSession(refreshToken: never, callback: (err: unknown, session: S | null) => void): void;
}

export function currentFreshSession<S extends SessionLike>(
  user: UserLike<S> | null,
  nowSeconds: () => number = () => Math.floor(Date.now() / 1000),
): Promise<S | null> {
  return new Promise((resolve) => {
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      // Same clock as isValid(): the server's, via the session's drift.
      const drift = session.getClockDrift?.() ?? 0;
      const secondsLeft = session.getIdToken().getExpiration() - (nowSeconds() - drift);
      if (secondsLeft > REFRESH_AHEAD_SECONDS) {
        resolve(session);
        return;
      }
      user.refreshSession(session.getRefreshToken() as never, (refreshErr, fresh) => {
        resolve(!refreshErr && fresh && fresh.isValid() ? fresh : session);
      });
    });
  });
}
